import { api as apiV2 } from '@/api/api.v2';
import api from '@/api';
import isEmpty from 'lodash/isEmpty';
import { getSettingsFromStateWell, restoreCurvesSettings } from './libs/getSettingsFromStateWell';
import { getWellLoggingKeysFromStateWell } from './libs/getWellLoggingKeysFromStateWell';
import { prepareActiveTabletColumns } from './libs/prepareStateWellColumns';

/*
    Общая модель для работы с состоянием скважин
*/

const initialState = {
    /*
        Список состояний
    */
    states: null,
    /*
        Привязка состояний скважин к самим скважинам по их id
    */
    wellsByState: null,

    isLoading: false,
    isError: null,
};

export const stateWellsModel = {
    name: 'stateWells',
    state: initialState,
    reducers: {
        setResponseData(state, payload) {
            return { ...state, states: payload.states, wellsByState: payload.wellsByState, isLoading: false };
        },
        startRequest(state) {
            return { ...state, isLoading: true, isError: false };
        },
        setError(state) {
            return { ...state, isLoading: false, isError: true };
        },
        setActiveTemplateId(state, activeTemplateId) {
            return { ...state, activeTemplateId };
        },
        setActiveGeoVersions(state, payload) {
            const { versions } = payload;

            return { ...state, activeGeoVersions: versions };
        },
    },
    effects: dispatch => ({
        /*
            Изменить настройки скважины согласно выбранному состоянию
        */

        async applyStateWell(payload, rootState) {
            const { stateId, well } = payload;
            const stateWell = await api.carottage.fetchProfilesData({ profileId: [stateId] });

            if (!stateWell || isEmpty(stateWell)) {
                // eslint-disable-next-line no-console
                console.log('Состояние скважины не было найдено или оно пустое');
                return;
            }
            if (stateWell) {
                const { ProfilesData } = stateWell;
                const currentWellState = ProfilesData[0].wells[0];
                currentWellState.params = restoreCurvesSettings(currentWellState);
                const { activeTemplateId } = rootState.templates;
                const { data } = rootState.researches;
                const researches = data[well.id];
                let currentVersions = [];
                let defaultSettings = {};

                if (rootState.tablets.isGroup) {
                    currentVersions = rootState.inspector.geoObjects[rootState.tablets.activeGroupName];
                    const activeTabletKey = rootState.tablets.list[rootState.tablets.activeGroupName][well.id]?.key;
                    defaultSettings = rootState.tabletSettings[activeTabletKey];
                } else {
                    currentVersions = rootState.inspector.geoObjects[well.id];
                    defaultSettings = rootState.tabletSettings[rootState.lists.activeItemKey];
                }

                const settings = getSettingsFromStateWell(currentWellState, defaultSettings, rootState);
                const { geoVersions, columnsOrder } = settings;
                this.setActiveGeoVersions({ versions: geoVersions });
                let wellLoggingKeys = [];
                let curveSettings = {};
                let curveDisplaySettings = {};

                if (researches && !isEmpty(researches)) {
                    const wellLoggingKeysAndCurvesFromStateWell = getWellLoggingKeysFromStateWell(
                        researches,
                        currentWellState,
                    );
                    wellLoggingKeys = wellLoggingKeysAndCurvesFromStateWell?.wellLoggingKeys ?? [];
                    curveSettings = wellLoggingKeysAndCurvesFromStateWell?.curveSettings ?? {};
                    curveDisplaySettings = wellLoggingKeysAndCurvesFromStateWell?.curveDisplaySettings ?? {};
                }

                const tabletSettingsPayload = {
                    data: Object.assign({}, settings, {
                        wellLoggingKeys: wellLoggingKeys,
                        curveSettings: curveSettings,
                        prevTemplateId: activeTemplateId,
                        curveDisplaySettings: curveDisplaySettings,
                        columnsOrder: columnsOrder,
                    }),
                    wellKey: well.key,
                };
                if (ProfilesData[0]?.depthType) {
                    const { depthType } = ProfilesData[0];
                    dispatch.displaySettings.setDepthType(depthType);
                }
                if (typeof ProfilesData[0]?.emptyColumnsDataSettings === 'boolean')
                    dispatch.displaySettings.updateDisplaySettings({
                        emptyColumnsDataSettings: ProfilesData[0]?.emptyColumnsDataSettings,
                    });

                dispatch.tabletSettings.setTabletSettings(tabletSettingsPayload);

                if (geoVersions?.length > 0) {
                    const mappedGeoVersionsArray = geoVersions
                        .map(v => {
                            const matchingVersion = currentVersions.find(o => o.stratumVersionId === v.id);

                            if (matchingVersion) {
                                return {
                                    ...matchingVersion,
                                    visible: v.visible,
                                };
                            }
                            return null;
                        })
                        .filter(Boolean);

                    const updatedGeoObjects = {
                        ...currentVersions,
                        [well.id]: mappedGeoVersionsArray,
                    };

                    dispatch.inspector.setGeoObjects(updatedGeoObjects);
                    dispatch.tablets.setTabletGeoObjects({ versions: mappedGeoVersionsArray });
                    geoVersions.forEach(ver =>
                        dispatch.tablets.toggleGeoObjectsVisible({ versionId: ver.id, visible: ver.visible }),
                    );
                }
            }
        },
        initStateWells(_, rootState) {
            const { states } = rootState.stateWells;

            if (!isEmpty(states)) return;

            dispatch.stateWells.getStateWells();
        },
        /*
            Получение состояний скважин
        */
        async getStateWells() {
            this.startRequest();

            try {
                const result = await apiV2.carottage.fetchStateWells();

                this.setResponseData(result);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('error::request::get_state_wells', error);
                this.setError(error);
            }
        },
        /*
            Сохранение состояние скважины
        */
        async createStateWell(payload, rootState) {
            const { wellId, wellKey, title, ...other } = payload;
            let geoVersions = [];
            if (rootState.tablets.isGroup)
                geoVersions = rootState.inspector.geoObjects[rootState.tablets.activeGroupName];
            else geoVersions = rootState.inspector.geoObjects[wellId];
            const settings = rootState.tabletSettings[wellKey];
            const researches = rootState.researches.data[wellId];
            const emptyColumnsDataSettings = rootState.displaySettings.emptyColumnsDataSettings;
            const globalCurvesSettings = rootState.displaySettings.globalCurvesSettings;
            const tooltipSettings = rootState.columnsTooltipProps;
            const depthType = rootState.displaySettings.depthType;

            const preparedState = prepareActiveTabletColumns(
                wellId,
                wellKey,
                settings,
                researches,
                geoVersions,
                emptyColumnsDataSettings,
                tooltipSettings,
                globalCurvesSettings,
                depthType,
            );

            const payloadRequest = {
                wells: [
                    {
                        columnWidth: preparedState.columnWidth,
                        params: preparedState.params,
                        wellId: wellId,
                    },
                ],
                activeGeoVersion: 0,
                tooltipSettings: preparedState.tooltipSettings,
                emptyColumnsDataSettings: preparedState.emptyColumnsDataSettings,
                globalCurvesSettings: preparedState.globalCurvesSettings,
                type: null, //type for states in LoggingProfile
                profileTitle: title,
                depthType: preparedState.depthType,
                isCorrelation: false, // isCorrelation for states in LoggingProfile
                isSyncScroll: false, // isSyncScroll for states in LoggingProfile
                ...other,
            };

            const body = JSON.stringify(payloadRequest);
            await apiV2.carottage.createStateWell(body);
            /*
                Серверное обновление
            */
            dispatch.stateWells.getStateWells();
        },
    }),
};
